import { useEffect, useRef } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';
import { v4 as uuidv4 } from 'uuid';

import useMapContext from 'components/Map/hooks/useMapContext';
import useMapEvents from 'components/Map/hooks/useMapEvents';

import { DEFAULT_CENTER, MAP_EVENT_TYPE, MAP_LAYER, POINT_MODE } from './constants';
import type { Feature, MapEventListener, MapVertexChangedEvent, Point, Position } from './types';

const useReticle = () => {
    const { createPointFeature } = featureUtil;

    const centerPointFeatureRef = useRef<Feature<Point> | undefined>(undefined);
    const innerCirclePerimeterFeatureRef = useRef<Feature<Point> | undefined>(undefined);
    const outerCirclePerimeterFeatureRef = useRef<Feature<Point> | undefined>(undefined);
    const vertexPositionRef = useRef<Position>(DEFAULT_CENTER);

    const { subscribe, unsubscribe } = useMapEvents();
    const { features, mapState } = useMapContext();
    const { addFeature, removeFeature, updateFeature } = features;

    const { pointMode } = mapState;
    const isEnabled = pointMode === POINT_MODE.RETICLE;

    useEffect(() => {
        const handleVertexChanged = (event: MapVertexChangedEvent) => {
            const { vertexPosition } = event.detail;

            vertexPositionRef.current = vertexPosition;

            updateReticlePosition(vertexPosition);
        };

        subscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged as MapEventListener);

        return () =>
            unsubscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged as MapEventListener);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isEnabled) {
            initializeReticle(vertexPositionRef.current);
        } else {
            removeReticle();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointMode]);

    const updateReticlePosition = (position: Position) => {
        if (
            !centerPointFeatureRef.current ||
            !innerCirclePerimeterFeatureRef.current ||
            !outerCirclePerimeterFeatureRef.current
        ) {
            return;
        }

        centerPointFeatureRef.current.geometry.coordinates = position;
        innerCirclePerimeterFeatureRef.current.geometry.coordinates = position;
        outerCirclePerimeterFeatureRef.current.geometry.coordinates = position;
        updateFeature(centerPointFeatureRef.current);
        updateFeature(innerCirclePerimeterFeatureRef.current);
        updateFeature(outerCirclePerimeterFeatureRef.current);
    };

    const removeReticle = () => {
        removeFeature(centerPointFeatureRef.current?.id as string);
        removeFeature(innerCirclePerimeterFeatureRef.current?.id as string);
        removeFeature(outerCirclePerimeterFeatureRef.current?.id as string);

        centerPointFeatureRef.current = undefined;
        innerCirclePerimeterFeatureRef.current = undefined;
        outerCirclePerimeterFeatureRef.current = undefined;
    };

    const initializeReticle = (vertexPosition: Position) => {
        const centerPointFeature = createPointFeature(vertexPosition, uuidv4());

        centerPointFeatureRef.current = centerPointFeature as Feature<Point>;
        addFeature({ feature: centerPointFeature, layerName: MAP_LAYER.RETICLE_CENTER_POINT });

        const innerCirclePerimeterFeature = createPointFeature(vertexPosition, uuidv4());

        innerCirclePerimeterFeatureRef.current = innerCirclePerimeterFeature;

        addFeature({
            feature: innerCirclePerimeterFeature,
            layerName: MAP_LAYER.RETICLE_INNER_CIRCLE_PERIMETER
        });

        const outerCirclePerimeterFeature = createPointFeature(vertexPosition, uuidv4());

        outerCirclePerimeterFeatureRef.current = outerCirclePerimeterFeature as Feature<Point>;

        addFeature({
            feature: outerCirclePerimeterFeature,
            layerName: MAP_LAYER.RETICLE_OUTER_CIRCLE_PERIMETER
        });
    };
};

export default useReticle;
